import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, Dialog, DialogContent, IconButton, Fade, Stack, Typography, useTheme } from '@mui/material'
import { ReactComponent as CloseIcon } from "src/v2/assets/icons/close-icon.svg"
import ChatbotService from 'src/services/chatbot-service'
import ReplayIcon from '@mui/icons-material/Replay'
import { ReactComponent as ArrowRightIcon } from "src/v2/assets/icons/arrow-right-icon.svg"
import TextFieldInput from '../Input/text-field-input'
import ButtonWithIcon from '../Button/Button'
import DatalakeService from 'src/services/datalake-service'
import { CHAT_HISTORY_KEY, CHAT_SESSION_KEY, CHAT_RELATED_KEY } from 'src/utils/constants'

const getCurrentTime = () => {
    const now = new Date()
    return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

const getInitialMessages = () => [
    {
        sender: 'bot',
        text: 'Olá! Em que posso ajudar?',
        time: getCurrentTime()
    }
]

const TypingIndicator = () => {
    const theme = useTheme()

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: 0.7 }}>
            <Typography variant="caption" sx={{ fontStyle: 'italic', color: theme.palette.primary.white70 }}>
                Digitando
            </Typography>
            <Box sx={{ display: 'flex', gap: '4px' }}>
                {[0, 1, 2].map((i) => (
                    <Box
                        key={i}
                        component="span"
                        sx={{
                            width: 5,
                            height: 5,
                            borderRadius: '50%',
                            backgroundColor: theme.palette.common.white,
                            animation: 'blink 1.4s infinite both',
                            animationDelay: `${i * 0.2}s`,
                            '@keyframes blink': { '50%': { opacity: 0.3 } },
                        }}
                    />
                ))}
            </Box>
        </Box>
    )
}

const ChatbotInModal = ({ open, onClose }) => {
    const theme = useTheme()
    const chatbotService = new ChatbotService()
    const datalakeService = new DatalakeService()

    const [activeTab, setActiveTab] = useState('chat')
    const [inputMessage, setInputMessage] = useState('')
    const [perguntasRelacionadas, setPerguntasRelacionadas] = useState(() => {
        const saved = localStorage.getItem(CHAT_RELATED_KEY)
        return saved ? JSON.parse(saved) : []
    })
    const [chatLoading, setChatLoading] = useState(false)
    const [reportLoading, setReportLoading] = useState(false)
    const [message_id, setMessageId] = useState(null)

    const [session_id, setSessionId] = useState(() => {
        return localStorage.getItem(CHAT_SESSION_KEY) || null
    })

    const scrollContainerRef = useRef(null)
    const savedScrollPosition = useRef(0)
    const [chatMessages, setChatMessages] = useState(() => {
        const saved = localStorage.getItem(CHAT_HISTORY_KEY)
        return saved ? JSON.parse(saved) : getInitialMessages()
    })
    const [reportMessages, setReportMessages] = useState([
        {
            sender: 'bot',
            text: 'Por favor, descreva melhor o erro ou inconsistência que encontrou para que nosso time de suporte possa analisar.',
            time: getCurrentTime()
        }
    ])

    useEffect(() => {
        // salva o id da sessão
        if (session_id) {
            localStorage.setItem(CHAT_SESSION_KEY, session_id)
        }

        // salva o histórico de mensagens
        if (chatMessages?.length > 0) {
            localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(chatMessages))
        }

        if (perguntasRelacionadas) {
            localStorage.setItem(CHAT_RELATED_KEY, JSON.stringify(perguntasRelacionadas))
        }
    }, [session_id, chatMessages, perguntasRelacionadas])

    const scrollToBottom = () => {
        setTimeout(() => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight
            }
        }, 0)
    }

    useEffect(() => {
        if (open) {
            scrollToBottom()
        }
    }, [open])

    const messages = activeTab === 'chat' ? chatMessages : reportMessages
    const setMessages = activeTab === 'chat' ? setChatMessages : setReportMessages
    const loading = activeTab === 'chat' ? chatLoading : reportLoading

    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

    const saveScrollPosition = () => {
        if (scrollContainerRef.current) {
            savedScrollPosition.current = scrollContainerRef.current.scrollTop
        }
    }

    const restoreScrollPosition = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = savedScrollPosition.current
        }
    }

    const sendBotMesssage = async (messageText) => {
        try {
            const currentSessionId = session_id || localStorage.getItem(CHAT_SESSION_KEY)

            const payload = { mensagem: messageText }

            if (currentSessionId) {
                payload.session_id = currentSessionId
            }

            const postResponse = await chatbotService.sendMessage(payload)
            const { message_id: newMessageId, session_id: newSessionId } = postResponse.body

            if (!newMessageId || !newSessionId) {
                throw new Error('Resposta inválida do chatbot')
            }

            setMessageId(newMessageId)
            setSessionId(newSessionId)

            return { message_id: newMessageId, session_id: newSessionId }
        } catch (error) {
            console.error("Erro ao enviar mensagem:", error)
            setMessages(prev => [...prev, {
                id: crypto.randomUUID(),
                sender: 'bot',
                time: getCurrentTime(),
                isError: true,
                errorType: 'POST',
                originalMessage: messageText
            }])
            return null
        }
    }

    const getBotResponse = async (message_id, session_id) => {
        const maxRetries = 5
        const retryDelay = 5000 // 5 segundos
        try {
            for (let i = 0; i < maxRetries; i++) {
                const getResponse = await chatbotService.getMessage(message_id, session_id)

                const body = getResponse.body

                if (body.status_code === 500) {
                    // a api sempre retorna status HTTP 200, mas o body pode ter um status_code 500
                    // nesse caso, mostramos a mensagem de erro do bot, visto que não faz sentido ficar tentando várias vezes
                    setMessages(prev => [...prev, {
                        id: crypto.randomUUID(),
                        sender: 'bot',
                        time: getCurrentTime(),
                        text: body.message || "Infelizmente ocorreu um erro interno e não consigo responder agora."
                    }])

                    // retornamos null para encerrar a função getBotResponse sem cair no catch
                    return null
                }

                const isSuccess = body?.messages?.length > 0

                if (isSuccess) {
                    return body.messages[0].data
                }
                if (i < maxRetries - 1) {
                    await sleep(retryDelay)
                }
            }
            throw new Error("O bot demorou muito para responder.")
        } catch (error) {
            setMessages(prev => [...prev, {
                id: crypto.randomUUID(),
                sender: 'bot',
                time: getCurrentTime(),
                isError: true,
                errorType: 'GET'
            }])
            return null
        }
    }

    const fetchBotResponse = async (messageText) => {
        setChatLoading(true)

        try {
            const postResponse = await sendBotMesssage(messageText)
            if (!postResponse) return

            const response = await getBotResponse(postResponse.message_id, postResponse.session_id)
            if (!response) return

            const botResponse = {
                id: message_id,
                sender: 'bot',
                text: response.mensagem,
                time: getCurrentTime()
            }


            setMessages((prev) => [...prev, botResponse])
            setPerguntasRelacionadas(response.perguntas_relacionadas || [])
            setTimeout(() => {
                setTimeout(restoreScrollPosition, 50)
            }, 50)

        } finally {

            setChatLoading(false)

        }
    }

    const handleReportError = async (userMessage) => {
        setMessages(prev => [...prev, userMessage])
        setInputMessage('')
        setReportLoading(true)

        let currentPath = window.location.hash
            ? window.location.hash.replace('#', '') 
            : window.location.pathname

        currentPath = currentPath.split('?')[0] 

        try {
            await datalakeService.postReportError(userMessage.text, currentPath)

            const successMessage = {
                id: crypto.randomUUID(),
                sender: 'bot',
                text: 'Seu report foi enviado com sucesso! Nossa equipe avaliará em breve',
                time: getCurrentTime()
            }
            setMessages(prev => [...prev, successMessage])
        } catch (error) {
            console.error("Erro ao enviar report:", error)
            const errorMessage = {
                id: crypto.randomUUID(),
                sender: 'bot',
                text: 'Ocorreu um erro ao enviar seu reporte. Por favor, tente novamente mais tarde.',
                time: getCurrentTime(),
            }
            setMessages(prev => [...prev, errorMessage])
        } finally {
            setReportLoading(false)
        }
    }


    const handleSendMessage = async () => {
        if (!inputMessage.trim()) return

        setPerguntasRelacionadas([])

        const userMessage = {
            id: crypto.randomUUID(),
            sender: 'user',
            text: inputMessage,
            time: getCurrentTime()
        }

        saveScrollPosition()

        if (activeTab === 'report') {
            await handleReportError(userMessage)
            return
        }


        const currentInput = inputMessage
        setMessages((prev) => [...prev, userMessage])
        setInputMessage('')

        await fetchBotResponse(currentInput)
    }

    const handleRetry = async () => {
        setChatLoading(true)
        saveScrollPosition()

        const errorToRetry = [...messages].reverse().find(msg => msg.isError)

        setMessages(prev => prev.filter(msg => msg.id !== errorToRetry.id))

        try {
            if (errorToRetry.errorType === 'POST') {
                await fetchBotResponse(errorToRetry.originalMessage || '')
            } else if (errorToRetry.errorType === 'GET') {
                const response = await getBotResponse(message_id, session_id)

                if (!response) {
                    return
                }

                const botResponse = {
                    id: message_id,
                    sender: 'bot',
                    text: response.mensagem,
                    time: getCurrentTime()
                }


                setMessages((prev) => [...prev, botResponse])
                setPerguntasRelacionadas(response.perguntas_relacionadas || [])
                setTimeout(() => {
                    setTimeout(restoreScrollPosition, 50)
                }, 50)

            }
        } catch (e) {
            console.error("Erro ao tentar novamente:", e)
            setMessages(prev => [
                ...prev, errorToRetry])

            setMessages(prev => [...prev, errorToRetry])
            setTimeout(() => {
                setTimeout(restoreScrollPosition, 50)
            }, 50)

        } finally {

            setChatLoading(false)
            setTimeout(restoreScrollPosition, 50)

        }

    }

    const handleClick = async (questionText) => {


        setPerguntasRelacionadas([])

        const userMessage = {
            id: crypto.randomUUID(),
            sender: 'user',
            text: questionText,
            time: getCurrentTime()
        }
        setMessages((prev) => [...prev, userMessage])
        saveScrollPosition()
        setInputMessage('')

        await fetchBotResponse(questionText)
    }


    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        position: 'fixed',
                        margin: 0,
                        maxWidth: 'none',
                        maxHeight: 'none',
                        top: { xs: '90px', sm: 0 },
                        bottom: 0,
                        right: 0,
                        left: { xs: 0, sm: 'auto' },
                        width: { xs: '100%', sm: 450 },
                        height: { xs: 'calc(100% - 90px)', sm: '100%' },

                        bgcolor: theme.palette.background.paper,
                        boxShadow: '-10px 0px 30px rgba(0,0,0,0.5)',
                        backgroundImage: 'none',
                        color: theme.palette.common.white,

                        borderRadius: {
                            xs: 0,
                            sm: 0
                        }
                    }
                }
                }
                TransitionComponent={Fade}
            >

                <Box sx={{
                    pt: 3,
                    pb: 2,
                    px: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                }}>

                    <IconButton
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            right: 24,
                            top: 24,
                            width: 24,
                            height: 24,
                            padding: 0,
                            color: theme.palette.icon.main,
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.05)'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ px: 3, pb: 2 }}>
                    <Stack direction="row" spacing={4} justifyContent="center" >
                        {['chat', 'report'].map((tab) => (
                            <Typography
                                key={tab}
                                variant="button"
                                onClick={() => { setActiveTab(tab); setInputMessage('') }}
                                sx={{
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    fontSize: '0.9rem',
                                    color: activeTab === tab ? theme.palette.common.white : theme.palette.primary.white70,
                                    borderBottom: activeTab === tab ? `3px solid ${theme.palette.icon.main}` : '3px solid transparent',
                                    pb: 1,
                                    px: 1,
                                    transition: '0.3s',
                                    '&:hover': { color: theme.palette.common.white }
                                }}
                            >
                                {tab === 'chat' ? 'TIRAR DÚVIDAS' : 'REPORTAR ERRO'}
                            </Typography>
                        ))}
                    </Stack>
                </Box>

                <DialogContent sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    pl: 3,
                    pt: 3,
                    pb: 3,
                    pr: 0,
                    height: '100%',
                    overflow: 'hidden'
                }}>

                    <Box sx={{
                        flex: 1,
                        overflowY: 'auto',
                        pr: 1,
                        display: 'flex',
                        flexDirection: 'column',

                        '&::-webkit-scrollbar': {
                            width: '6px',
                            backgroundColor: 'transparent'
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'transparent',
                            borderRadius: '3px'
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                            borderRadius: '3px',
                            transition: 'background-color 0.3s'
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.3)'
                        },

                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgba(255, 255, 255, 0.15) transparent'
                    }}
                        ref={scrollContainerRef}
                    >
                        <Stack spacing={3} sx={{ pb: 2, pr: 2 }}>
                            {messages.map((msg, index) => {
                                const isUser = msg.sender === 'user'
                                const isError = msg.isError


                                if (isError) {
                                    return (
                                        <Box
                                            key={msg.id || `err-${index}`}
                                            sx={{
                                                border: `1px solid ${theme.palette.error.main}`,
                                                bgcolor: 'rgba(224, 29, 29, 0.1)',
                                                borderRadius: 2,
                                                p: 2,
                                                maxWidth: '90%',
                                                alignSelf: 'flex-start',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 1
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ color: theme.palette.error.main, fontWeight: 'bold' }}>
                                                Ocorreu um erro
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: theme.palette.primary.white70 }}>
                                                {msg.text || "Não foi possível obter uma resposta no momento."}
                                            </Typography>

                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={handleRetry}
                                                startIcon={<ReplayIcon sx={{ width: 16, height: 16 }} />}
                                                sx={{
                                                    mt: 0.5,
                                                    fontSize: '0.75rem',
                                                    textTransform: 'none',
                                                    alignSelf: 'flex-start',
                                                    borderColor: theme.palette.error.main,
                                                    color: theme.palette.common.white,
                                                    '&:hover': {
                                                        bgcolor: theme.palette.error.main,
                                                        color: theme.palette.common.white
                                                    }
                                                }}
                                            >
                                                Tentar Novamente
                                            </Button>
                                        </Box>
                                    )
                                }

                                if (isUser) {
                                    return (
                                        <Box
                                            key={msg.id || index}
                                            sx={{
                                                alignSelf: 'flex-end',
                                                maxWidth: '273px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'flex-end'
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    bgcolor: theme.palette.info.main,
                                                    color: theme.palette.common.white,
                                                    padding: '8px 16px',
                                                    gap: '0px',
                                                    borderRadius: '12px 0px 12px 12px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                }}
                                            >
                                                <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '12px', mb: 0 }}>
                                                    {msg.time}
                                                </Typography>
                                                <Typography variant="body2" sx={{ lineHeight: 1.5, fontSize: '14px' }}>
                                                    {msg.text}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )
                                }

                                return (
                                    <Box
                                        key={msg.id || index}
                                        sx={{
                                            alignSelf: 'flex-start',
                                            maxWidth: '85%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start'
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ color: theme.palette.primary.white70, fontSize: '0.7rem', mb: 0.5, ml: 1 }}>
                                            {msg.time}
                                        </Typography>
                                        <Box sx={{ px: 1 }}>
                                            <Typography variant="body2" sx={{ lineHeight: 1.6, fontSize: '0.95rem', color: theme.palette.common.white }}>
                                                {msg.text}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )
                            })}

                            {loading && <TypingIndicator />}
                        </Stack>

                        {activeTab === 'chat' && perguntasRelacionadas.length > 0 && (
                            <Box mt={3} sx={{ animation: 'fadeIn 0.5s' }}>
                                <Typography variant="body2" sx={{ color: theme.palette.common.white, mb: 2 }}>
                                    Qual dessas opções te ajudam mais?
                                </Typography>
                                <Stack spacing={1.5} alignItems="flex-start">
                                    {perguntasRelacionadas.map((question, index) => (
                                        <Button
                                            key={index}
                                            variant="outlined"
                                            onClick={() => handleClick(question)}
                                            sx={{
                                                color: theme.palette.common.white,
                                                borderColor: theme.palette.input.borderColor.white70 || '#FFFFFFB2',
                                                borderRadius: '50px',
                                                textTransform: 'none',
                                                padding: '6px 20px',
                                                fontSize: '0.85rem',
                                                justifyContent: 'flex-start',
                                                '&:hover': {
                                                    borderColor: theme.palette.icon.white,
                                                    color: theme.palette.icon.main,
                                                    bgcolor: theme.palette.transparent.white10
                                                }
                                            }}
                                        >
                                            {question}
                                        </Button>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Box>

                    <Box sx={{
                        mt: 2,
                        pt: 2,
                        pb: 3,
                        pr: 3,
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                    }}>

                        <TextFieldInput
                            label="Mensagem"
                            placeholder="Mensagem"
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                            disabled={loading}
                            variant="outlined"

                            // força o estado "ativo" do label, mantendo o placeholder visível, mesmo antes de clicar no input
                            InputLabelProps={{ shrink: true }}

                            sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                    height: '48px',
                                    color: theme.palette.common.white,
                                    backgroundColor: 'transparent',
                                    borderRadius: 0,
                                    '& fieldset': {
                                        borderColor: theme.palette.input.borderColor.white || '#535870',
                                        borderWidth: '1px'
                                    },
                                    '&:hover fieldset': { borderColor: theme.palette.common.white },
                                    '&.Mui-focused fieldset': { borderColor: theme.palette.common.white },
                                    '&.Mui-disabled fieldset': {
                                        borderColor: theme.palette.input.borderColor.white || '#535870'
                                    }
                                },
                                '& input': {
                                    fontFamily: '"Titillium Web", sans-serif',
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: '48px',
                                    height: '48px',
                                    padding: '0 12px',
                                    boxSizing: 'border-box',
                                },
                                '& input::placeholder': {
                                    color: theme.palette.primary.white70,
                                    opacity: 1,
                                    fontFamily: '"Titillium Web", sans-serif',
                                    fontSize: '14px',
                                    lineHeight: '48px',
                                },
                                // correção do autocomplete do navegador
                                '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
                                    WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.paper} inset !important`,
                                    WebkitTextFillColor: `${theme.palette.common.white} !important`,
                                    caretColor: theme.palette.common.white,
                                    borderRadius: '0px'
                                },

                                //oculta a estrutura do label
                                '& .MuiInputLabel-root': {
                                    display: 'none'
                                },
                                '& .MuiOutlinedInput-notchedOutline legend': {
                                    display: 'none',
                                    width: 0
                                }
                            }}
                        />

                        <ButtonWithIcon
                            disabled={loading || !inputMessage.trim()}
                            onClick={handleSendMessage}
                            sx={{
                                width: '58px',
                                height: '40px',
                                minWidth: 'unset',
                                padding: 0,
                                borderRadius: '19px',
                                color: theme.palette.icon.main,

                                '&:hover': {
                                    backgroundColor: theme.palette.transparent.white10,
                                    color: theme.palette.icon.main
                                },
                                '&.Mui-disabled': {
                                    borderColor: theme.palette.icon.disabled,
                                    color: theme.palette.icon.disabled,
                                }
                            }}
                        >
                            <ArrowRightIcon fontSize="small" />
                        </ButtonWithIcon>
                    </Box>

                </DialogContent>
            </Dialog>
        </>
    )
}

ChatbotInModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
}

export default ChatbotInModal
